<template>
  <div v-if="forceUpdate">
    <v-card>
      <v-card-title>
        {{ editTitle }}
      </v-card-title>
      <v-card-text>
        <v-container v-if="editedItem" class="pl-0 pr-0">
          <v-alert
            v-if="editedItem['defaultBilling']"
            border="bottom"
            colored-border
            type="warning"
            elevation="2"
          >
            This is your default billing address. You cannot make changes to
            this address other than setting it as your default shipping address.
            If you need to make changes to your billing address, please contact
            our trading desk.
          </v-alert>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  :disabled="editedItem['defaultBilling']"
                  v-model="editedItem['location.label']"
                  label="Address Nickname"
                  :rules="[rules.required]"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  :disabled="editedItem['defaultBilling']"
                  v-model="editedItem['location.attention']"
                  label="Attn:"
                  :rules="[rules.maxLength]"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  :disabled="editedItem['defaultBilling']"
                  v-model="editedItem['location.addressee']"
                  label="Addressee"
                  :rules="[rules.maxLength]"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" v-if="!manualEdit">
                <address-auto-complete
                  :id="dynamicID()"
                  v-on:placechanged="getAddressData"
                  @keypress="clearAddressData"
                >
                </address-auto-complete>
              </v-col>
              <v-col cols="12" sm="4" md="4">
                <v-checkbox
                  v-model="manualEdit"
                  label="Edit Manually"
                  dense
                ></v-checkbox>
              </v-col>
              <v-col cols="12" sm="8" md="8" v-if="manualEdit">
                <v-select
                  :items="getCountries()"
                  v-model="editedItem['location.country']"
                  label="Country"
                  :rules="[rules.required, rules.maxLength]"
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" v-if="manualEdit">
                <v-text-field
                  :disabled="editedItem['defaultBilling']"
                  v-model="editedItem['location.street1']"
                  label="Street"
                  :rules="[rules.required, rules.containsStreetNumber, rules.maxLength]"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" v-if="manualEdit">
                <v-text-field
                  :disabled="editedItem['defaultBilling']"
                  v-model="editedItem['location.street2']"
                  label="Street (Line 2)"
                  :rules="[rules.maxLength]"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" md="4" v-if="manualEdit">
                <v-text-field
                  :disabled="editedItem['defaultBilling']"
                  v-model="editedItem['location.city']"
                  label="City"
                  :rules="[rules.required, rules.maxLength]"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" md="4" v-if="manualEdit">
                <v-text-field
                  v-if="getStates(editedItem['location.country']).length === 0"
                  :disabled="editedItem['defaultBilling']"
                  v-model="editedItem['location.state']"
                  label="State"
                  :rules="[rules.required]"
                  dense
                ></v-text-field>
                <v-select
                  v-else
                  :items="getStates(editedItem['location.country'])"
                  v-model="editedItem['location.state']"
                  label="State"
                  :rules="[rules.required]"
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" sm="4" md="4" v-if="manualEdit">
                <v-text-field
                  :disabled="editedItem['defaultBilling']"
                  v-model="editedItem['location.zip']"
                  label="Zip"
                  :rules="[rules.required]"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="5" md="5">
                <number-field
                  :disabled="editedItem['defaultBilling']"
                  v-model="editedItem['location.phone']"
                  label="Phone"
                  dense
                  type="text"
                  :id="dynamicID()"
                  :options="{ type: `phone` }"
                ></number-field>
              </v-col>
              <v-col cols="12" sm="3" md="3">
                <v-text-field
                  v-model="editedItem['companyPin']"
                  :append-icon="showPin ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showPin = !showPin"
                  :rules="[rules.required]"
                  :type="showPin ? 'text' : 'password'"
                  label="Company Pin"
                  @keyup="showPin = false"
                  dense
                  :id="dynamicID()"
                  hint="Please call the Trading Desk if you forgot your Company Pin."
                  :persistent-hint="true"
                ></v-text-field>
              </v-col>
              <v-col cols="12" v-if="!hideDefaultShippingCheckbox">
                <v-checkbox
                  v-model="editedItem['defaultShipping']"
                  label="Default Shipping"
                  hint="Check this box to make this the primary shipping address for your account"
                  :persistent-hint="true"
                  dense
                ></v-checkbox>
              </v-col>
              <v-col cols="12" v-if="showAddressBookOption()">
                <v-checkbox
                  v-model="editedItem['addToAddressBook']"
                  label="Add to address book"
                  hint="Check this box to add this address to your address book"
                  :persistent-hint="true"
                  dense
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="white" @click="close" class="mr-2 mb-1"> Cancel</v-btn>
        <v-btn
          color="primary"
          class="mb-1 lighten-1"
          @click="save"
          :loading="loading"
          :disabled="!valid || loading"
        >
          {{ editedItem['id'] ? 'Save Address' : 'Add New Address' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>
<script>
import {mapGetters} from 'vuex'
import _ from 'lodash'
import NProgress from 'nprogress'
import Vue from 'vue'
import AddressAutoComplete from '@/components/company/AddressAutoComplete'
import Util from '@/util'
import {UNITED_STATES} from '@/util/constants'
import companyMixin from '@/mixins/company'
import {default as orderMixin} from '@/mixins/order'
import NumberField from '@/components/NumberField'

export default {
  mixins: [companyMixin, orderMixin],
  props: [
    'selectedAddress',
    'hideDefaultShippingCheckbox',
    'addToAddressBookOption',
  ],
  components: {
    AddressAutoComplete,
    NumberField
  },
  watch: {
    selectedAddress: {
      handler() {
        this.setup()
      },
    },
  },
  data() {
    return {
      manualEdit: false,
      forceUpdate: false,
      valid: true,
      total: 0,
      options: {},
      editTitle: '',
      editedItem: {
        companyPin: '',
        id: null,
        'location.id': null,
        companyId: null,
        'location.label': null,
        'location.attention': null,
        'location.addressee': null,
        'location.street1': null,
        'location.street2': null,
        'location.city': null,
        'location.state': null,
        'location.zip': null,
        'location.country': null,
        'location.phone': null,
        defaultBilling: false,
        defaultShipping: false,
        addToAddressBook: true,
      },
      showPin: true,
      rules: {
        required: (value) => !!value || 'This field is required',
        containsStreetNumber: (value) =>
          value && value.match(/\d+/g) ? true : 'Must contain a street number',
        maxLength: (value) => !value || value.length <= orderMixin.data().ADDRESS_CHAR_LIMIT || `Cannot exceed ${orderMixin.data().ADDRESS_CHAR_LIMIT} characters`,
      },
      loading: false
    }
  },
  mounted() {
    this.setup()
  },
  computed: {
    ...mapGetters(['getSelectedCompany', 'getConfig']),
  },
  methods: {
    showAddressBookOption() {
      const matchingCountry = this.getMatchingCountry(
        this.editedItem[`location.country`]
      )
      return (
        this.addToAddressBookOption &&
        matchingCountry &&
        matchingCountry.states.length > 0
      )
    },
    trimEditedItem() {
      const paths = [
        'location.label',
        'location.attention',
        'location.addressee',
        'location.street1',
        'location.street2',
        'location.city',
        'location.state',
        'location.zip',
        'location.country',
        'location.phone',
      ]
      for (let path of paths)
        if (
          this.editedItem[path] &&
          typeof this.editedItem[path] === `string`
        ) {
          this.editedItem[path] = this.editedItem[path].trim()
        }
    },
    dynamicID: Util.dynamicID,
    stateCompare(str1, str2) {
      return (
        `${str1}`.toUpperCase().replaceAll(`.`, ``) ===
        `${str2}`.toUpperCase().replaceAll(`.`, ``)
      )
    },
    clearAddressData() {
      this.editedItem = {...this.editedItem,
        'location.street1': ``,
        'location.street2': ``,
        'location.city': ``,
        'location.state': ``,
        'location.zip': ``,
        'location.country': UNITED_STATES,
      }
    },
    async getAddressData(data) {
      const matchingCountries = this.getCountries().filter(
        (country) =>
          data.country === country.text || data.country === country.value
      )
      const country =
        matchingCountries.length > 0
          ? matchingCountries[0].value
          : UNITED_STATES
      const matchingStates = this.getStates(country).filter(
        (state) =>
          this.stateCompare(data.administrative_area_level_1, state.value) ||
          this.stateCompare(data.administrative_area_level_2, state.value) ||
          this.stateCompare(data.administrative_area_level_1, state.text) ||
          this.stateCompare(data.administrative_area_level_2, state.text)
      )
      this.editedItem = {...this.editedItem,
        'location.street1': `${_.get(data, 'street_number', 'XXX')} ${_.get(
          data,
          'route',
          ''
        )}`,
        'location.street2': data.subpremise,
        'location.city': data.locality || data.postal_town,
        'location.state':
          matchingStates.length > 0
            ? matchingStates[0].value
            : data.administrative_area_level_1,
        'location.zip': data.postal_code,
        'location.country': country,
      }
      if (
        !this.addressDataComplete()
      ) {
        this.manualEdit = true
        await Vue.nextTick()
        this.$refs.form.validate()
      }
    },
    newAddress() {
      return {
        'location.id': 0,
        'location.country': `US`,
        companyId: this.getSelectedCompany.id,
        addToAddressBook: true,
      }
    },
    async setup() {
      this.forceUpdate = false
      await Vue.nextTick()
      if (_.get(this, '$refs.form')) this.$refs.form.reset()
      if (!this.selectedAddress || this.selectedAddress.id === undefined) {
        this.manualEdit = false
        this.editedItem = Object.assign({}, this.newAddress())
        this.editTitle = 'New Address'
      } else {
        this.manualEdit = true
        this.editedItem = Object.assign({}, this.selectedAddress)
        this.editTitle = 'Edit Address'
      }
      if (this.addToAddressBookOption) this.editedItem.addToAddressBook = false
      if (Object.keys(this.getConfig('COUNTRIES')).length === 0)
        await this.$store.dispatch('fetchCountries')
      this.forceUpdate = true
    },
    close() {
      this.editedItem = {}
      this.$emit('close')
    },
    addressDataComplete() {
      const requiredFields = [
        `location.country`, `location.street1`, `location.city`, `location.state`, `location.zip`
      ];
      if (this.rules.containsStreetNumber(this.editedItem[`location.street1`]) !== true) return false
      for (let requiredField of requiredFields)
        if (_.get(this.editedItem, requiredField) === undefined || _.get(this.editedItem, requiredField) === null || _.get(this.editedItem, requiredField) === ``) return false
      return true
    },
    async save() {
      this.loading = true
      this.trimEditedItem()
      if (!this.manualEdit && !this.addressDataComplete()) this.manualEdit = true //open up all address
      await Vue.nextTick()
      // If we are not providing the add to address book option, add to address book by default
      if (!this.showAddressBookOption()) this.editedItem.addToAddressBook = true
      if (this.$refs.form.validate()) {
        try {
          NProgress.start()
          await this.$store.dispatch('saveCompanyAddress', this.editedItem)
          window._VMA.snackbar = {
            show: true,
            text: 'Address Updated',
            color: 'green',
          }
          this.$emit('save')
        } catch (err) {
          window._VMA.snackbar = {
            show: true,
            text: err.response.data,
            color: 'red',
          }
        }
        NProgress.done()
      }
      this.loading = false
    },
  },
}
</script>
